import React, { useState, useEffect, useRef } from "react";
import classes from "./TypeDiagnosisResult.module.scss";
import { useNavigate } from "react-router-dom";
import QuestionHeader from "../../organism/questionHeader/QuestionHeader";
import {
  DiagnosisResults,
  fetchDiagnosis,
} from "../../api/UsersDiagnosisApi";
import { State } from "../../api/State";
import SimpleButton from "../../atoms/simple-button/SimpleButton";
import BreadCrumb from "../../molecules/breadcrumb/BreadCrumb";
import TeacherComment from "../../molecules/teacher-commnet/TeacherComment";
import rabbitNormal from "../../static/images/rabbit-normal.png";

// ロゴファイルの動的読み込み
const logos = Array.from({ length: 12 }).map((_, index) => {
  const typeNumber = index + 1;
  return require(`../../static/images/typeDiagnosis/resultLogo${typeNumber}.png`);
});

const messageLogos = Array.from({ length: 9 }).map((_, index) => {
  const typeNumber = index + 1;
  return require(`../../static/images/typeDiagnosis/messageLogo${typeNumber}.png`);
});

const getResultLogoPath = (diagnosisType: string | undefined) => {
  if (diagnosisType == null) {
    return logos[0];
  }
  const typeNumber = Number(diagnosisType.replace("type", ""));
  return logos[typeNumber - 1];
};

const getMessgaeLogoPath = (typeNumber: number) => {
  return messageLogos[typeNumber - 1];
};

// テキスト文章の改行コード変換
const getNewLineText = (text: string | undefined) => {
  return text?.split("\n").map((str, index, array) => {
    if (str === "") {
      return (
        <React.Fragment key={`empty-${index}`}>
          <br key={`br1-${index}`} />
        </React.Fragment>
      );
    }
    return (
      <span key={`line-${index}`}>
        {str}
        {index !== array.length - 1 && <br />}
      </span>
    );
  });
};

const getColorClassForChild = (type: string, currentType: string | undefined) => {
  if (currentType != null && type === currentType) {
    switch (type) {
      case "Rule": return "blue";
      case "Balance": return "green";
      case "Free": return "orange";
      default: return "gray";
    }
  }
  return "gray";
};

const getColorClassForMoney = (type: string, currentType: string | undefined) => {
  if (currentType != null && type === currentType) {
    switch (type) {
      case "Aggressive": return "orange";
      case "Balance": return "green";
      case "Conservative": return "blue";
      default: return "gray";
    }
  }
  return "gray";
};

const TypeDiagnosisResult = () => {
  const navigate = useNavigate();

  const [diagnosisResult, setDiagnosisResult] = useState<State<DiagnosisResults>>();

  const fetchDiagnosisApi = fetchDiagnosis();

  useEffect(() => {
    fetchDiagnosisApi.fetch();
  }, []);
  
  useEffect(() => {
    if (fetchDiagnosisApi.state?.data) {
      setDiagnosisResult(fetchDiagnosisApi.state);
    }
  }, [fetchDiagnosisApi.state]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className={classes.topContainer}>
        {/* ヘッダー */}
        <div className={classes.headerContainer}>
          <QuestionHeader />
          <div className={classes.typeContainer}>
            <div className={classes.subTitle}>
              {diagnosisResult?.data?.[0]?.typeTitle}
            </div>
            <span className={classes.mainTitle}>
              {diagnosisResult?.data?.[0]?.typeName}
            </span>
            <span className={classes.subType}>タイプ</span>
          </div>
          <div className={classes.typeExplanation}>
            <div className={classes.typeFuture}>
              {diagnosisResult?.data?.[0]?.typeFeature}
            </div>
          </div>
          <div className={classes.logoContainer}>
            <img
              src={getResultLogoPath(diagnosisResult?.data?.[0]?.diagnosisType)}
              alt="Logo"
            />
          </div>
        </div>

        {/* 子育て 資産形成の傾向 */}
        <div className={classes.barContainer}>
          <div className={classes.barTitle}>子育てのスタイル</div>
          <div className={classes.bar}>
            <div className={`${classes.barLeft} ${classes[getColorClassForChild("Rule", diagnosisResult?.data?.[0]?.childTendency)]}`}>ルール</div>
            <div className={`${classes.barCenter} ${classes[getColorClassForChild("Balance", diagnosisResult?.data?.[0]?.childTendency)]}`}>バランス</div>
            <div className={`${classes.barRight} ${classes[getColorClassForChild("Free", diagnosisResult?.data?.[0]?.childTendency)]}`}>フリー</div>
          </div>

          <div className={classes.barTitle}>資産形成のスタイル</div>
          <div className={classes.bar}>
            <div className={`${classes.barLeft} ${classes[getColorClassForMoney("Conservative", diagnosisResult?.data?.[0]?.moneyTendency)]}`}>守り</div>
            <div className={`${classes.barCenter} ${classes[getColorClassForMoney("Balance", diagnosisResult?.data?.[0]?.moneyTendency)]}`}>バランス</div>
            <div className={`${classes.barRight} ${classes[getColorClassForMoney("Aggressive", diagnosisResult?.data?.[0]?.moneyTendency)]}`}>攻め</div>
          </div>
        </div>

        {/* 子育ての傾向 */}
        <div id="education-tendency" className={classes.messageBoxContainer}>
          <div className={classes.messageBox}>
            <h2 className={classes.messageBoxTitle}>子育ての傾向</h2>
            <div className={classes.logoContainer}>
              <img src={getMessgaeLogoPath(2)} alt="Logo" />
            </div>
            <h3 className={classes.messageBoxSubtitle}>
              {diagnosisResult?.data?.[0]?.childTendTitle}
            </h3>
            <p className={classes.messageBoxContent}>
              {getNewLineText(diagnosisResult?.data?.[0]?.childTendExpl)}
            </p>
          </div>
        </div>

        {/* 資産形成の傾向 */}
        <div id="asset-tendency" className={classes.messageBoxContainer}>
          <div className={classes.messageBox}>
            <h2 className={classes.messageBoxTitle}>資産形成の傾向</h2>
            <div className={classes.logoContainer}>
              <img src={getMessgaeLogoPath(1)} alt="Logo" />
            </div>
            <h3 className={classes.messageBoxSubtitle}>
              {diagnosisResult?.data?.[0]?.moneyTendTitle}
            </h3>
            <p className={classes.messageBoxContent}>
              {getNewLineText(diagnosisResult?.data?.[0]?.moneyTendExpl)}
            </p>
          </div>
        </div> 

        {/* おすすめアクション */}
        <div className={classes.messageBoxContainer}>
          <div id="recommend-action" className={classes.messageBox}>
            <h2 className={classes.messageBoxTitle}>おすすめアクション</h2>
            <div className={classes.logoContainer}>
              <img src={getMessgaeLogoPath(5)} alt="Logo" />
            </div>
            <div className={classes.messageSubBoxContent}>
              <p className={classes.messageSubBoxTitle}>子育てのヒント</p>
              <p className={classes.messageSubBoxTitleExplanation}>
                {diagnosisResult?.data?.[0]?.childActionTitle}
              </p>
            </div>
            <p className={classes.messageBoxContent}>
              {getNewLineText(diagnosisResult?.data?.[0]?.childActionExpl)}
            </p>
            <div className={classes.messageSubBoxContent}>
              <p className={classes.messageSubBoxTitle}>資産形成のヒント</p>
              <p className={classes.messageSubBoxTitleExplanation}>
                {diagnosisResult?.data?.[0]?.moneyActionTitle}
              </p>
            </div>
            <p className={classes.messageBoxContent}>
              {getNewLineText(diagnosisResult?.data?.[0]?.moneyActionExpl)}
            </p>
            <div className={classes.messageSubBoxContent}>
              <p className={classes.messageSubBoxTitle}>金融教育のヒント</p>
              <p className={classes.messageSubBoxTitleExplanation}>
                {diagnosisResult?.data?.[0]?.moneyEducationTitle}
              </p>
            </div>
            <p className={classes.messageBoxContent}>
              {getNewLineText(diagnosisResult?.data?.[0]?.moneyEducationExpl)}
            </p>
          </div>
        </div>

        {/* コメント＆ボタン */}
        <div className={classes.blockContainer}>
          <TeacherComment
            image={rabbitNormal}
            comment={[
              "結果はいかがでしたか？",
              "今回の結果を資産づくりにお役立ていただけると嬉しいです♪",
            ]}
          />
        </div>
        <div className={classes.diagnosisButton}>
          <SimpleButton
            label="もう一度診断してみる"
            width="80%"
            color="black"
            isRadius={true}
            onClick={() => navigate("/type9-diagnosis")}
          />
        </div>
        <div className={classes.myPageButton}>
          <SimpleButton
            label="TOPページ"
            width="80%"
            color="orange"
            isRadius={true}
            onClick={() => navigate("/top")}
          />
        </div>

        <BreadCrumb
          data={[
            { displayName: "みらいいコンシェル", url: "/top" },
            { displayName: "子育てとおカネの9タイプ診断結果", url: "/diagnosis-result" },
          ]}
        />
      </div>
    </>
  );
};

export default TypeDiagnosisResult;
